import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from './authService';

// Get user from local storage with token validation
const getUserFromStorage = () => {
  try {
    const user = localStorage.getItem('user');
    if (!user) return null;
    
    const parsedUser = JSON.parse(user);
    
    // Check if token exists and is not expired
    if (parsedUser?.token) {
      const tokenParts = parsedUser.token.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        
        // If token is expired, clear storage and return null
        if (payload.exp && (payload.exp * 1000) < Date.now()) {
          console.log('Stored token expired, clearing storage');
          localStorage.removeItem('user');
          sessionStorage.removeItem('user');
          return null;
        }
      }
    }
    
    return parsedUser;
  } catch (error) {
    console.error('Error parsing user from localStorage:', error);
    localStorage.removeItem('user');
    return null;
  }
};

const initialState = {
  user: getUserFromStorage(), // Now safely validates before loading
  isError: false,
  isSuccess: false,
  isLoading: false,
  message: '',
  isEmailSentSuccess: false,
  isVerified: false,
};

// Register User
export const register = createAsyncThunk(
  'auth/register',
  async (userData, thunkAPI) => {
    try {
      console.log('authSlice.register called');
      return await authService.register(userData);
    } catch (error) {
      console.error('Registration error in slice:', error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Resend Verification Email
export const resendVerificationEmail = createAsyncThunk(
  'auth/resendVerificationEmail',
  async (email, thunkAPI) => {
    try {
      console.log('authSlice.resendVerificationEmail called');
      return await authService.resendVerificationEmail(email);
    } catch (error) {
      console.error('Resend verification error in slice:', error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Email Verification
export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token, thunkAPI) => {
    try {
      console.log('authSlice.verifyEmail called');
      const response = await authService.verifyEmail(token);
      return response;
    } catch (error) {
      console.error('Email verification error in slice:', error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Log In
export const login = createAsyncThunk(
  'auth/login',
  async (userData, thunkAPI) => {
    try {
    //   console.log('authSlice.login called with:', { 
    //     email: userData.email, 
    //     hasPassword: !!userData.password 
    //   });
      return await authService.login(userData);
    } catch (error) {
      console.error('Login error in slice:', error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Logout - add proper try/catch
export const logout = createAsyncThunk('auth/logout', async (_, thunkAPI) => {
  try {
    authService.logout();
    return null;
  } catch (error) {
    console.error('Logout error in slice:', error.message);
    return thunkAPI.rejectWithValue(error.message);
  }
});

// Newsletter signup
export const newsletterSignup = createAsyncThunk(
  'auth/newsletterSignup',
  async (email, thunkAPI) => {
    try {
      console.log('authSlice.newsletterSignup called');
      return await authService.newsletterSignup(email);
    } catch (error) {
      console.error('Newsletter signup error in slice:', error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Get user profile - add missing console.log and proper try/catch structure
export const getProfile = createAsyncThunk(
  'auth/getProfile',
  async (_, thunkAPI) => {
    try {
      console.log('authSlice.getProfile called');
      return await authService.getProfile();
    } catch (error) {
      console.error('Get profile error in slice:', error.message);
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// Auth Slice
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    reset: (state) => {
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = '';
      state.isEmailSentSuccess = false;
    },
    clearUser: (state) => {
      state.user = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.isVerified = false;
      state.message = '';
      localStorage.removeItem('user');
    },
    updateUserVerification: (state, action) => {
      if (state.user) {
        state.user.verified = action.payload;
        state.isVerified = action.payload;
        localStorage.setItem('user', JSON.stringify(state.user));
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Register cases
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload;
        state.isVerified = action.payload?.verified || false;
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
      })
      
      // Resend verification cases
      .addCase(resendVerificationEmail.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.isEmailSentSuccess = false;
      })
      .addCase(resendVerificationEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isEmailSentSuccess = true;
        state.message = action.payload?.message || 'Verification email sent successfully';
      })
      .addCase(resendVerificationEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.isEmailSentSuccess = false;
      })
      
      // Verify email cases
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isVerified = true;
        if (state.user) {
          state.user.verified = true;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
        state.message = action.payload?.message || 'Email verified successfully';
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      
      // Login cases
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
        state.message = '';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.user = action.payload;
        state.isVerified = action.payload?.verified || false;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
        state.user = null;
      })
      
      // Logout cases
      .addCase(logout.fulfilled, (state) => {
        state.user = null;
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = false;
        state.isVerified = false;
        state.message = '';
      })
      
      // Get profile cases
      .addCase(getProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.user) {
          state.user = { ...state.user, ...action.payload.user };
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset, clearUser, updateUserVerification } = authSlice.actions;
export default authSlice.reducer;